import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showAPI, riderAPI } from '../utils/api';
import { ArrowLeft, CalendarBlank, MapPin, EnvelopeSimple, DownloadSimple, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../utils/api';

import { EntrySuccess } from '../components/entry/EntrySuccess';
import { ClassSelector } from '../components/entry/ClassSelector';
import { AccommodationsSection } from '../components/entry/AccommodationsSection';
import { SpotPicker } from '../components/entry/SpotPicker';

const EntryForm = () => {
  const { id: showId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entryCounts, setEntryCounts] = useState({});
  const [riderName, setRiderName] = useState('');
  const [horseName, setHorseName] = useState('');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedRollovers, setSelectedRollovers] = useState({});
  const [practiceCounts, setPracticeCounts] = useState({});  // { [classId]: number } for warm-up/exhibition multi-runs
  const [rolloverMap, setRolloverMap] = useState({});
  const [riderAgeGroup, setRiderAgeGroup] = useState('');
  const [stallsRequested, setStallsRequested] = useState(0);
  const [rvRequested, setRvRequested] = useState(0);
  const [stallPricing, setStallPricing] = useState('weekend');
  const [rvPricing, setRvPricing] = useState('weekend');
  const [accommodations, setAccommodations] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdEntries, setCreatedEntries] = useState([]);
  const [entryResult, setEntryResult] = useState(null);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  // Free-text "special request" the rider attaches to this entry submission
  // (e.g. "Bringing a stallion — needs end stall" or "Please run my 2D
  // horse first"). Travels with every class entry created in this batch so
  // the producer sees the same note on each row in Manage Entries.
  const [specialRequest, setSpecialRequest] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [activeDayTab, setActiveDayTab] = useState('');
  const [managedRiders, setManagedRiders] = useState([]);
  const [savedHorses, setSavedHorses] = useState([]);
  const [selectedRiderId, setSelectedRiderId] = useState('self');
  const [quickProfile, setQuickProfile] = useState(null);
  const [quickApplied, setQuickApplied] = useState(false);
  // v270 — Anonymous / logged-in riders can opt to save their rider &
  // horse names to this device's localStorage so the next entry form
  // auto-fills without a login. When the user has explicitly saved
  // info previously, we pre-populate the fields on mount.
  const [saveMyInfo, setSaveMyInfo] = useState(false);
  const [hasSavedInfo, setHasSavedInfo] = useState(false);
  // Surfaces the rider's prior paid total for this show so we can render
  // a reassurance banner above the totals box: "You've already paid
  // $X.XX for this show — this checkout is for new charges only."
  // Populated by my-checkout-status which already gates other once-per-show
  // fees (processing fee, tie-out) — same source of truth.
  const [alreadyPaidTotal, setAlreadyPaidTotal] = useState(0);
  const [lastPaidSessionId, setLastPaidSessionId] = useState(null);
  const [entries, setEntries] = useState([]);  // Array of { riderName, horseName, selectedClasses, selectedRollovers, orgId }
  const [spotSelection, setSpotSelection] = useState({ spots: [], pricingType: 'weekend', nights: 1, totalFee: 0 });
  // Effective service fee % for THIS show, resolved on the backend from
  // (show.service_fee_pct override → platform default). Fetched on load
  // so the totals preview matches what Stripe will actually charge.
  const [serviceFeePct, setServiceFeePct] = useState(0.04);
  // Optional rider opt-in for Target Race side-pots that have a producer-set fee.
  const [targets, setTargets] = useState([]);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);
  // Per-target category picks: { [target_id]: [category_id, ...] }
  // When a rider opts into a target, we default to ALL categories on that
  // target being checked (most permissive UX). The rider can uncheck any
  // categories they don't want their times to count toward.
  const [targetCategoryPicks, setTargetCategoryPicks] = useState({});
  // Producer-configured merch / add-on fees.
  // T-shirt selection now supports multiple line-items so a rider can order
  // (e.g.) 2× M + 1× XL + 1× 2XL in one go. Each entry is {qty, size}; the
  // unit price (set on the show) applies uniformly. Legacy single-item
  // shape `{enabled, qty, size}` is preserved on submit for back-compat
  // with addon aggregates that already key on `qty`/`size`.
  const [tshirtSelection, setTshirtSelection] = useState({ enabled: false, items: [{ qty: 1, size: '' }] });
  // Tie-out fee is auto-billed per horse without a stall × days at the show
  // (no rider opt-in). State only tracks whether the rider has manually
  // overridden the default to zero (legacy; kept for code parity but unused).
  const [shavingsQty, setShavingsQty] = useState(0);
  // Optional inline sponsorship — rider tips the producer with a custom $ amount
  // that's added to their bill. Doc is created with payment_status=PENDING and
  // flipped to PAID after the rider's entry payment lands.
  const [sponsorEnabled, setSponsorEnabled] = useState(false);
  const [sponsorAmount, setSponsorAmount] = useState('');
  // Rider self-opt-out of the auto-billed tie-out fee (e.g. "I already
  // have a stall not on this checkout"). Producer can also flip this
  // server-side via Manage Entries → Waive Tie-Out.
  const [tieOutWaived, setTieOutWaived] = useState(false);
  // Once-per-show processing fee policy: when the rider has already paid
  // processing on a prior checkout for this show, we suppress the line
  // from the on-form preview (server also enforces this at checkout).
  const [procFeeAlreadyPaid, setProcFeeAlreadyPaid] = useState(false);
  // Existing stall reservations the rider already has for this show (paid
  // OR pending). Counted into totalStalls so tie-out is auto-waived when
  // they've already locked in a stall on a prior checkout.
  const [existingStallCount, setExistingStallCount] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadShow(); loadOrgs(); loadProfile(); loadCheckoutStatus(); }, [showId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) setRiderName(prev => prev || user.name || ''); }, [user]);

  // v270 — Pre-fill rider + horse from device localStorage when the
  // rider previously ticked "Save my info". Runs once on mount and
  // only fills empty fields, so /profile quick-fill or the user
  // typing still wins.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('sm_saved_rider_info') || 'null');
      if (saved && (saved.rider_name || saved.horse_name)) {
        setHasSavedInfo(true);
        setSaveMyInfo(true);
        setRiderName(prev => prev || saved.rider_name || '');
        setHorseName(prev => prev || saved.horse_name || '');
      }
    } catch { /* corrupt localStorage — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCheckoutStatus = async () => {
    if (!showId) return;
    try {
      const { data } = await api.get(`/shows/${showId}/my-checkout-status`);
      setProcFeeAlreadyPaid(!!data.processing_fee_already_paid);
      if (data.tie_out_waived) setTieOutWaived(true);
      setExistingStallCount(parseInt(data.existing_stall_count, 10) || 0);
      setAlreadyPaidTotal(Number(data.already_paid_total) || 0);
      setLastPaidSessionId(data.last_session_id || null);
    } catch { /* not logged in or no prior checkout — safe to ignore */ }
  };

  const loadOrgs = async () => {
    try { const { data } = await api.get('/organizations/public'); setOrganizations(data); } catch { /* ignore */ }
  };

  const loadProfile = async () => {
    try {
      const { data } = await riderAPI.quickEnterProfile();
      setQuickProfile(data);
      setManagedRiders(data.managed_riders || []);
      setSavedHorses(data.horses || []);
    } catch { /* ignore */ }
  };

  const loadShow = async () => {
    try {
      const { data: showData } = await showAPI.get(showId);
      setShow(showData);
      const { data: counts } = await api.get(`/shows/${showId}/entry-counts`);
      setEntryCounts(counts);
      const { data: roMap } = await api.get(`/shows/${showId}/rollover-map`);
      setRolloverMap(roMap.rollover_map || {});
      const { data: accom } = await api.get(`/shows/${showId}/accommodations`);
      setAccommodations(accom);
      // Fetch the show's effective service-fee % so the totals preview
      // matches what Stripe will actually charge (custom override or
      // platform default). Best-effort; falls back to 4% on failure.
      try {
        const { data: feeRes } = await api.get(`/shows/${showId}/effective-service-fee`);
        if (typeof feeRes?.service_fee_pct === 'number') setServiceFeePct(feeRes.service_fee_pct);
      } catch { /* fall back to default 4% */ }
      try {
        const { data: tgts } = await api.get(`/shows/${showId}/targets`);
        setTargets(Array.isArray(tgts) ? tgts : []);
      } catch { setTargets([]); }
    } catch { toast.error('Failed to load show'); }
    finally { setLoading(false); }
  };

  const toggleClass = (classId) => {
    setSelectedClasses(prev => {
      const isRemoving = prev.includes(classId);
      if (isRemoving) {
        setSelectedRollovers(ro => { const updated = { ...ro }; delete updated[classId]; return updated; });
        setPracticeCounts(pc => { const updated = { ...pc }; delete updated[classId]; return updated; });
        return prev.filter(id => id !== classId);
      }
      return [...prev, classId];
    });
  };

  const toggleRollover = (parentClassId, rolloverClassId) => {
    setSelectedRollovers(prev => {
      const current = prev[parentClassId] || [];
      const isSelected = current.includes(rolloverClassId);
      return { ...prev, [parentClassId]: isSelected ? current.filter(id => id !== rolloverClassId) : [...current, rolloverClassId] };
    });
  };

  const handleAgeGroupChange = (newGroup) => {
    setRiderAgeGroup(newGroup);
    setSelectedRollovers(prev => {
      const updated = {};
      for (const [parentId, roIds] of Object.entries(prev)) {
        const rollovers = rolloverMap[parentId] || [];
        const filtered = roIds.filter(roId => {
          const ro = rollovers.find(r => r.id === roId);
          if (!ro) return false;
          return !ro.rollover_category || ro.rollover_category === newGroup;
        });
        if (filtered.length > 0) updated[parentId] = filtered;
      }
      return updated;
    });
  };

  const getEligibleRollovers = (parentClassId) => {
    const allRollovers = rolloverMap[parentClassId] || [];
    return allRollovers.filter(ro => {
      if (!ro.rollover_category) return true;
      if (!riderAgeGroup) return false;
      return ro.rollover_category === riderAgeGroup;
    });
  };

  const showDays = accommodations?.show_days || 1;
  const stallCostWeekend = accommodations?.stalls?.price || 0;
  const stallCostPerDay = accommodations?.stalls?.price_per_day || 0;
  const rvCostWeekend = accommodations?.rv_hookups?.price || 0;
  const rvCostPerDay = accommodations?.rv_hookups?.price_per_day || 0;

  const stallCost = stallsRequested * (stallPricing === 'per_day' && stallCostPerDay > 0 ? stallCostPerDay * showDays : stallCostWeekend);
  const rvCost = rvRequested * (rvPricing === 'per_day' && rvCostPerDay > 0 ? rvCostPerDay * showDays : rvCostWeekend);
  const spotFees = spotSelection.totalFee || 0;
  const accommodationFees = stallCost + rvCost + spotFees;
  const addEntry = () => {
    if (!riderName.trim()) { toast.error('Rider name is required'); return; }
    if (selectedClasses.length === 0) { toast.error('Select at least one class'); return; }
    setEntries(prev => [...prev, {
      riderName: riderName.trim(), horseName: horseName.trim(),
      selectedClasses: [...selectedClasses], selectedRollovers: { ...selectedRollovers },
      orgId: selectedOrgId || null
    }]);
    toast.success(`${riderName.trim()}${horseName.trim() ? ` on ${horseName.trim()}` : ''} added to entries`);
    // Reset for next entry
    setRiderName(user?.name || '');
    setHorseName('');
    setSelectedClasses([]);
    setSelectedRollovers({});
    setSelectedOrgId('');
    setRiderAgeGroup('');
    setQuickApplied(false);
  };

  const removeEntry = (index) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
    toast.info('Entry removed');
  };

  // Calculate fees for a single entry set
  const calcEntryFees = (classes, rollovers, practiceCountsMap = {}) => {
    const cf = classes.reduce((sum, cid) => {
      const cls = show?.classes.find(c => c.id === cid);
      if (!cls) return sum;
      const fee = parseFloat(cls.entry_fee || 0);
      // Practice classes (warm-up / exhibition) bill per-run when the rider
      // selects more than one. Standard classes ignore the count.
      const isPractice = ['warmup', 'exhibition'].includes(cls.class_type);
      const runs = isPractice ? Math.max(1, parseInt(practiceCountsMap[cid] || 1, 10)) : 1;
      return sum + fee * runs;
    }, 0);
    const rf = Object.entries(rollovers).reduce((sum, [parentId, roIds]) => {
      const roClasses = rolloverMap[parentId] || [];
      return sum + roIds.reduce((s, roId) => { const roCls = roClasses.find(c => c.id === roId); return s + (roCls ? parseFloat(roCls.entry_fee || 0) : 0); }, 0);
    }, 0);
    return { classFees: cf, rolloverFees: rf, rolloverCount: Object.values(rollovers).reduce((s, ids) => s + ids.length, 0) };
  };

  // Current form fees
  const currentFees = calcEntryFees(selectedClasses, selectedRollovers, practiceCounts);
  const classFees = currentFees.classFees;
  const rolloverFees = currentFees.rolloverFees;
  const totalRolloverCount = currentFees.rolloverCount;

  // Entry fees (added entries)
  const entriesFees = entries.reduce((sum, entry) => {
    const f = calcEntryFees(entry.selectedClasses, entry.selectedRollovers);
    return sum + f.classFees + f.rolloverFees;
  }, 0);
  const entriesClassCount = entries.reduce((sum, e) => sum + e.selectedClasses.length, 0);
  const entriesRolloverCount = entries.reduce((sum, e) => sum + Object.values(e.selectedRollovers).reduce((s, ids) => s + ids.length, 0), 0);

  // Count unique days entered (for processing fee calculation)
  const getDaysEntered = (classIds) => {
    const days = new Set();
    for (const cid of classIds) {
      const cls = show?.classes.find(c => c.id === cid);
      if (cls?.scheduled_day) days.add(cls.scheduled_day);
    }
    return Math.max(days.size, 1); // At least 1 day
  };

  const allSelectedClassIds = [
    ...selectedClasses,
    ...entries.flatMap(e => e.selectedClasses)
  ];
  const daysEntered = getDaysEntered(allSelectedClassIds);
  const baseProcessingFee = parseFloat(show?.processing_fee || 0);
  // When the producer set processing_fee_type='weekend' we charge it once,
  // flat, regardless of how many days/horses the rider entered. Default
  // 'per_day' (legacy) keeps the previous ×daysEntered behavior.
  const processingFeeType = (show?.processing_fee_type || 'per_day').toLowerCase();
  const processingFee = (baseProcessingFee > 0 && !procFeeAlreadyPaid)
    ? (processingFeeType === 'weekend' ? baseProcessingFee : baseProcessingFee * Math.max(1, daysEntered))
    : 0;
  // Target Race entry fees (sidepot-style opt-in). Only targets with an entry_fee > 0
  // and selected by the rider count. Single-payment across all riders.
  const targetFees = selectedTargetIds.reduce((sum, tid) => {
    const t = targets.find(t => t.id === tid);
    return sum + (t ? parseFloat(t.entry_fee || 0) : 0);
  }, 0);
  const tshirtTotalQty = tshirtSelection.enabled
    ? (tshirtSelection.items || []).reduce((sum, it) => sum + Math.max(0, parseInt(it.qty, 10) || 0), 0)
    : 0;
  const tshirtTotal = tshirtTotalQty > 0 ? Number(show?.tshirt_price || 0) * tshirtTotalQty : 0;

  // Tie-Out Fee — auto-billed per horse that does NOT have a stall, per day
  // at the show. Stall count includes both numeric `stallsRequested` and any
  // interactive SpotPicker stalls. RV hookups do NOT count as horse housing.
  const numHorses = entries.length + (riderName.trim() && selectedClasses.length > 0 ? 1 : 0);
  const interactiveStallCount = spotSelection.spots.filter(s => s.type === 'stall').length;
  const totalStalls = (parseInt(stallsRequested, 10) || 0) + interactiveStallCount + existingStallCount;
  // Tie-out fee is owed for horses that don't have a stall covered by
  // a PRE-EXISTING reservation (paid or pending). Stalls being purchased
  // in this same checkout are billed as a separate accommodation line —
  // they do NOT reduce tie-out for the on-form preview. This prevents
  // the counterintuitive "I added a stall and my total went down" when a
  // producer's tie-out × days happens to be more than their stall price.
  // (Producer can still flip the waive toggle in Manage Entries, and the
  // rider can still untick the auto-bill via the checkbox below.)
  const horsesNeedingTieOut = Math.max(0, numHorses - existingStallCount);
  const tieOutEnabled = !!show?.tie_out_fee_enabled && Number(show?.tie_out_fee_price) > 0;
  const tieOutTotal = (tieOutEnabled && !tieOutWaived)
    ? horsesNeedingTieOut * daysEntered * Number(show.tie_out_fee_price)
    : 0;

  const shavingsTotal = shavingsQty > 0 ? Number(show?.shavings_price_per_bag || 0) * shavingsQty : 0;
  // Optional inline sponsorship — when toggled on, add the rider's chosen
  // amount to the bill (validated against the show's sponsorship_minimum).
  const sponsorTotal = sponsorEnabled ? Math.max(0, parseFloat(sponsorAmount) || 0) : 0;
  const subtotal = classFees + rolloverFees + accommodationFees + entriesFees + processingFee + targetFees + tshirtTotal + tieOutTotal + shavingsTotal + sponsorTotal;
  // Service fee must use the SHOW'S effective rate (fetched from
  // /effective-service-fee on load), not a hardcoded 4%. Without this
  // the on-form preview disagreed with the actual Stripe charge for
  // any show with a custom platform-fee override.
  const serviceFee = Math.round(subtotal * serviceFeePct * 100) / 100;
  const stripeFee = subtotal + serviceFee > 0 ? Math.round(((subtotal + serviceFee) * 0.029 + 0.30) * 100) / 100 : 0;
  const totalFees = subtotal + serviceFee + stripeFee;

  const handleSubmit = async () => {
    if (!user) { toast.error('Please log in to enter'); navigate('/login'); return; }

    // Block submit if a Target Race is selected but the rider hasn't picked
    // any categories on it (zero is an invalid opt-in — backend would reject).
    // Single-category targets auto-pick that one and need no UI.
    for (const tid of selectedTargetIds) {
      const tgt = targets.find(t => t.id === tid);
      if (!tgt) continue;
      const cats = tgt.categories || [];
      if (cats.length === 0) continue;
      const defaultCat = cats[0]?.id;
      const picks = targetCategoryPicks[tid] || (defaultCat ? [defaultCat] : []);
      if (picks.length === 0) {
        toast.error(`Pick a category for "${tgt.name}" or uncheck it`);
        return;
      }
    }

    // Validate sponsorship amount against the show's minimum, if rider opted in.
    if (sponsorEnabled) {
      const minSponsor = Number(show?.sponsorship_minimum || 1) || 1;
      const amt = parseFloat(sponsorAmount);
      if (!Number.isFinite(amt) || amt < minSponsor) {
        toast.error(`Minimum sponsorship for this show is $${minSponsor.toFixed(2)}`);
        return;
      }
    }

    // Single-category enforcement: when a target has multiple categories,
    // the rider must select exactly one. UI defaults to the first eligible
    // category, but they could clear it on us — guard anyway.
    for (const tid of selectedTargetIds) {
      const tgt = targets.find(t => t.id === tid);
      if (!tgt) continue;
      const cats = tgt.categories || [];
      if (cats.length <= 1) continue;
      const picks = targetCategoryPicks[tid] || [];
      if (picks.length !== 1) {
        toast.error(`Pick exactly one category for "${tgt.name}"`);
        return;
      }
    }

    // Allow submitting with NO classes when the rider is only booking stalls/RV.
    const hasAccommodationsOnly = spotSelection.spots.length > 0 || stallsRequested > 0 || rvRequested > 0;
    const allEntries = [...entries];
    if (riderName.trim() && selectedClasses.length > 0) {
      allEntries.push({
        riderName: riderName.trim(), horseName: horseName.trim(),
        selectedClasses: [...selectedClasses], selectedRollovers: { ...selectedRollovers },
        practiceCounts: { ...practiceCounts },
        orgId: selectedOrgId || null
      });
    }

    // v270 — Persist rider/horse to device localStorage if the rider
    // checked "Save my info". Do it BEFORE the API call so a network
    // failure still leaves the fields ready for their retry. If they
    // unchecked the box and had info saved, clear it out.
    try {
      if (saveMyInfo && riderName.trim()) {
        localStorage.setItem('sm_saved_rider_info', JSON.stringify({
          rider_name: riderName.trim(),
          horse_name: horseName.trim(),
        }));
      } else if (!saveMyInfo && hasSavedInfo) {
        localStorage.removeItem('sm_saved_rider_info');
      }
    } catch { /* private mode / quota — non-fatal */ }
    // Stalls/RV-only path: no rider-entry call, just reserve spots and bail out to success.
    if (allEntries.length === 0) {
      if (!hasAccommodationsOnly) {
        toast.error('Add at least one class, stall, or RV hookup');
        return;
      }
      if (!riderName.trim()) { toast.error('Rider name is required'); return; }
      setSubmitting(true);
      try {
        if (spotSelection.spots.length > 0) {
          await api.post(`/shows/${showId}/reserve-spots`, {
            spots: spotSelection.spots,
            rider_name: riderName.trim(),
            user_id: user?.id || '',
            pricing_type: spotSelection.pricingType,
            nights: spotSelection.pricingType === 'per_day' ? spotSelection.nights : null
          });
        }
        setCreatedEntries([]);
        setEntryResult({ entries_created: 0, entries: [], accommodations_only: true });
        setSubmitted(true);
        toast.success('Stalls/RV reserved — proceed to payment');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to reserve spots');
      }
      finally { setSubmitting(false); }
      return;
    }

    setSubmitting(true);
    try {
      let totalCreated = 0;
      let lastData = null;
      const allCreatedEntries = [];
      for (const entry of allEntries) {
        // Only attach addons (merch/tie-out) to the FIRST submitted entry so
        // it isn't charged once per horse when multi-horse rider submits.
        const isFirst = entry === allEntries[0];
        const addons = isFirst ? {
          tshirt: tshirtSelection.enabled && tshirtTotalQty > 0 ? {
            // New shape — items[] preserves per-size breakdown so producers
            // see "2× M + 1× XL" instead of a collapsed size.
            items: (tshirtSelection.items || [])
              .filter((it) => (parseInt(it.qty, 10) || 0) > 0)
              .map((it) => ({ qty: Math.max(1, parseInt(it.qty, 10) || 1), size: (it.size || '').trim() })),
            // Legacy aggregate fields — show summary code already reads these.
            qty: tshirtTotalQty,
            size: (tshirtSelection.items || [])
              .filter((it) => (parseInt(it.qty, 10) || 0) > 0)
              .map((it) => it.size || '—')
              .join(', '),
            unit_price: Number(show?.tshirt_price || 0),
            total: tshirtTotal,
            label: show?.tshirt_label || 'Show T-Shirt',
          } : null,
          tie_out: tieOutTotal > 0 ? {
            qty: horsesNeedingTieOut,
            days: daysEntered,
            unit_price: Number(show?.tie_out_fee_price || 0),
            label: show?.tie_out_fee_label || 'Tie-Out Fee',
          } : null,
          shavings: shavingsQty > 0 ? {
            qty: shavingsQty,
            unit_price: Number(show?.shavings_price_per_bag || 0),
            label: show?.shavings_label || 'Shavings',
          } : null,
        } : null;
        const { data } = await api.post(`/shows/${showId}/rider-entry`, {
          rider_name: entry.riderName, horse_name: entry.horseName,
          class_ids: entry.selectedClasses, rollover_selections: entry.selectedRollovers,
          practice_counts: entry.practiceCounts || {},
          stalls_requested: stallsRequested, rv_hookups_requested: rvRequested,
          stall_pricing: stallPricing, rv_pricing: rvPricing,
          org_id: entry.orgId,
          addons,
          tie_out_waived: tieOutWaived,
          special_request: specialRequest,
        });
        totalCreated += data.entries_created;
        allCreatedEntries.push(...data.entries);
        lastData = data;
        // Only send accommodations on first entry
        if (stallsRequested > 0 || rvRequested > 0) {
          setStallsRequested(0); setRvRequested(0);
        }
      }
      setCreatedEntries(allCreatedEntries);
      setEntryResult({ ...lastData, entries: allCreatedEntries, entries_created: totalCreated });

      // Opt rider(s) into selected Target Races. v230: opt-ins are keyed by
      // (rider, horse) — a rider submitting 2 horses on the target pays 2
      // fees and gets 2 opt-in records. Producer marks each PAID after
      // collecting. We send the rider's chosen category so the results
      // computation only counts times from that category.
      if (selectedTargetIds.length > 0) {
        for (const tid of selectedTargetIds) {
          const tgt = targets.find(t => t.id === tid);
          const cats = tgt?.categories || [];
          const eligibleCats = cats.filter(c => c.class_ids.some(cid => selectedClasses.includes(cid)));
          const defaultCat = (eligibleCats[0] || cats[0])?.id;
          const picks = targetCategoryPicks[tid] || (defaultCat ? [defaultCat] : []);
          for (const entry of allEntries) {
            const hn = (entry.horseName || '').trim();
            // Skip opt-in if we don't have a horse — an unnamed opt-in would
            // create a mystery row in the roster the producer can't tell
            // apart from other entries by the same rider.
            if (!hn) continue;
            try {
              await api.post(`/shows/${showId}/targets/${tid}/opt-in`, {
                rider_id: user?.id,
                rider_name: (entry.riderName || riderName.trim() || user?.name || ''),
                horse_name: hn,
                selected_category_ids: picks.length ? picks : undefined,
              });
            } catch { /* best-effort opt-in */ }
          }
        }
      }

      // Inline sponsorship — best-effort. Creates a PENDING record tied to
      // this rider; the producer notification fires once the rider's entry
      // payment is confirmed (handled server-side in payments._mark_paid).
      if (sponsorEnabled && sponsorTotal > 0) {
        try {
          await api.post(`/shows/${showId}/sponsorships`, {
            amount: sponsorTotal,
            sponsor_name: riderName.trim() || user?.name || '',
            sponsor_email: user?.email || '',
          });
        } catch { /* best-effort */ }
      }

      // Reserve selected spots if any
      if (spotSelection.spots.length > 0) {
        try {
          await api.post(`/shows/${showId}/reserve-spots`, {
            spots: spotSelection.spots,
            rider_name: allEntries[0].riderName,
            user_id: user?.id || '',
            pricing_type: spotSelection.pricingType,
            nights: spotSelection.pricingType === 'per_day' ? spotSelection.nights : null
          });
        } catch { /* spot reservation is best-effort */ }
      }

      setSubmitted(true);
      setEntries([]);
      toast.success(`${totalCreated} ${totalCreated === 1 ? 'entry' : 'entries'} submitted for ${allEntries.length} rider${allEntries.length > 1 ? 's' : ''}!`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit entries'); }
    finally { setSubmitting(false); }
  };

  const handlePayOnline = async () => {
    try {
      // Commit the rider's just-submitted draft entries + spots BEFORE
      // starting the payment flow. Until this fires the producer doesn't
      // see them (so unpaid people can't ghost-reserve indefinitely).
      try {
        await api.post(`/shows/${showId}/rider-entry/commit`);
      } catch { /* non-fatal — webhook on PAID will also reconcile */ }
      // Class entries created? pay via the entry checkout.
      if (createdEntries.length > 0) {
        const firstEntryId = createdEntries[0].id;
        const { data } = await api.post(`/payments/square/checkout?entry_id=${firstEntryId}&origin_url=${window.location.origin}`);
        if (data.url) { window.location.href = data.url; return; }
      }
      // Stalls/RV-only path: pay for accommodations without any class entry.
      const { data } = await api.post('/payments/square/accommodations-checkout', {
        show_id: showId,
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment setup failed. Please try again or contact the producer.');
    }
  };

  const applyQuickEnter = () => {
    if (!quickProfile || !show) return;
    // Set rider name and horse
    setRiderName(quickProfile.last_rider_name || user?.name || '');
    setHorseName(quickProfile.last_horse_name || '');
    // Pre-select classes that match frequently entered class names
    const matchingClassIds = [];
    for (const cls of show.classes) {
      if (quickProfile.frequent_classes.some(fc => cls.name.toLowerCase().includes(fc.toLowerCase()) || fc.toLowerCase().includes(cls.name.toLowerCase()))) {
        matchingClassIds.push(cls.id);
      }
    }
    if (matchingClassIds.length > 0) setSelectedClasses(matchingClassIds);
    setQuickApplied(true);
    toast.success('Quick Enter applied! Review classes and submit.');
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="shimmer h-12 w-12 rounded-full"></div></div>;
  if (!show) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><p className="text-[#9E9E9E]">Show not found</p></div>;

  if (submitted) {
    return <EntrySuccess
      show={show}
      createdEntries={createdEntries}
      entryResult={entryResult}
      accommodations={accommodations}
      totalFees={totalFees}
      onPayOnline={handlePayOnline}
      targetFees={targetFees}
      sponsorTotal={sponsorTotal}
      spotSelection={spotSelection}
      accommodationFees={accommodationFees}
      stallCost={stallCost}
      rvCost={rvCost}
      spotFees={spotFees}
      processingFee={processingFee}
      addonFees={tshirtTotal + tieOutTotal + shavingsTotal}
      serviceFee={serviceFee}
      stripeFee={stripeFee}
    />;
  }

  const isSingleDayShow = show.start_date === show.end_date;
  // Producer-locked days: classes scheduled on these dates are hidden from
  // the rider entry form (e.g. finals day where entry happens by qualification).
  const lockedDays = new Set(show.locked_days || []);
  const visibleClasses = show.classes.filter(c => !((c.scheduled_date || '') && lockedDays.has(c.scheduled_date)));
  const hiddenLockedCount = show.classes.length - visibleClasses.length;
  const classesByDay = {};
  visibleClasses.forEach(cls => {
    const day = (!isSingleDayShow && cls.scheduled_day) ? cls.scheduled_day : 'All Days';
    if (!classesByDay[day]) classesByDay[day] = [];
    classesByDay[day].push(cls);
  });
  const dayKeys = Object.keys(classesByDay);
  const isMultiDay = dayKeys.length > 1 || (dayKeys.length === 1 && dayKeys[0] !== 'All Days');
  const isAccepting = show.state === 'PUBLISHED' || show.state === 'LIVE';
  const onlineEntriesDisabled = !!show.online_entries_disabled;
  const entryDeadline = show.entry_deadline ? new Date(show.entry_deadline) : null;
  const isDeadlinePassed = entryDeadline && new Date() > entryDeadline;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-14 sm:h-16 flex items-center">
          <button onClick={() => navigate('/shows')} className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors text-sm" data-testid="back-to-shows-btn">
            <ArrowLeft size={18} /> <span>Back to Shows</span>
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-black text-white mb-2 sm:mb-3" style={{fontFamily: 'Outfit'}} data-testid="entry-show-name">{show.name}</h1>
          <div className="flex flex-wrap gap-3 sm:gap-4 text-[#9E9E9E] text-xs sm:text-sm">
            <span className="flex items-center gap-1.5"><CalendarBlank size={14} /> {new Date(show.start_date).toLocaleDateString()} - {new Date(show.end_date).toLocaleDateString()}</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} /> {show.location}</span>
          </div>
          {entryDeadline && !isDeadlinePassed && (
            <p className="mt-2 text-xs text-[#FF9500] font-semibold" data-testid="entry-deadline-notice">
              Online entries close: {entryDeadline.toLocaleDateString()} at {entryDeadline.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </p>
          )}
        </div>

        {!isAccepting || isDeadlinePassed ? (
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-8 text-center" data-testid="entries-closed-notice">
            <p className="text-[#9E9E9E] text-lg">{isDeadlinePassed ? 'Online entries for this show have closed.' : 'This show is not currently accepting entries.'}</p>
            {isDeadlinePassed && entryDeadline && (
              <p className="text-[#555] text-sm mt-2">Entry deadline was {entryDeadline.toLocaleDateString()} at {entryDeadline.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
            )}
          </div>
        ) : onlineEntriesDisabled ? (
          <div
            className="bg-[#161616] border border-[#FF9500]/30 rounded-lg p-8 text-center"
            data-testid="mail-in-only-notice"
          >
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-[#FF9500]/15 rounded-full">
              <EnvelopeSimple size={26} weight="bold" className="text-[#FF9500]" />
            </div>
            <h2 className="text-white text-xl font-bold mb-2" style={{fontFamily: 'Outfit'}}>
              Mail-In Entries Only
            </h2>
            <p className="text-[#9E9E9E] text-sm max-w-md mx-auto mb-5">
              Online entries are turned off for this show. Please download the printable
              entry form below, fill it out, and mail it in with your payment.
            </p>
            {(show.check_payable_to || show.check_mailing_address) && (
              <div className="mb-5 max-w-md mx-auto bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-left text-xs">
                {show.check_payable_to && (
                  <p className="text-[#9E9E9E]">
                    <span className="text-white font-semibold">Make check payable to:</span> {show.check_payable_to}
                  </p>
                )}
                {show.check_mailing_address && (
                  <p className="text-[#9E9E9E] mt-1 whitespace-pre-line">
                    <span className="text-white font-semibold">Mail to:</span>
                    {'\n'}{show.check_mailing_address}
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/shows/${show.id}/printable-entry-form.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF9500] hover:bg-[#FF9500]/90 text-black font-semibold rounded-md text-sm transition-colors"
                data-testid="mail-in-download-btn"
              >
                <DownloadSimple size={16} weight="bold" /> Download Entry Form PDF
              </a>
              <button
                type="button"
                onClick={() => {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/shows/${show.id}/printable-entry-form.pdf`;
                  const w = window.open(url, '_blank');
                  if (w) {
                    setTimeout(() => { try { w.focus(); w.print(); } catch { /* user can Cmd+P */ } }, 1500);
                  }
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-transparent hover:bg-[#FF9500]/10 text-[#FF9500] border border-[#FF9500]/40 font-semibold rounded-md text-sm transition-colors"
                data-testid="mail-in-print-btn"
              >
                <Printer size={16} weight="bold" /> Print
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Quick Enter Banner */}
            {quickProfile?.has_history && !quickApplied && !submitted && (
              <div className="mb-6 bg-gradient-to-r from-[#0096FF]/10 to-[#00D4FF]/5 border border-[#0096FF]/30 rounded-lg p-4 sm:p-5" data-testid="quick-enter-banner">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: 'Outfit' }}>Quick Enter</h3>
                    <p className="text-[#9E9E9E] text-xs sm:text-sm mt-0.5">
                      Auto-fill with <span className="text-white font-semibold">{quickProfile.last_rider_name}</span>
                      {quickProfile.last_horse_name && <> on <span className="text-[#FF9500] font-semibold">{quickProfile.last_horse_name}</span></>}
                      {quickProfile.frequent_classes.length > 0 && <> &bull; {quickProfile.frequent_classes.length} usual class{quickProfile.frequent_classes.length !== 1 ? 'es' : ''} detected</>}
                    </p>
                  </div>
                  <button
                    onClick={applyQuickEnter}
                    className="px-5 py-2.5 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-bold text-sm rounded-lg transition-colors whitespace-nowrap"
                    data-testid="quick-enter-apply-btn"
                  >
                    Quick Enter
                  </button>
                </div>
              </div>
            )}

            {quickApplied && (
              <div className="mb-4 bg-[#34C759]/10 border border-[#34C759]/30 rounded-lg px-4 py-2.5 flex items-center justify-between" data-testid="quick-enter-applied">
                <p className="text-[#34C759] text-xs font-semibold">Quick Enter applied — review your selections below</p>
                <button onClick={() => { setQuickApplied(false); setSelectedClasses([]); setRiderName(user?.name || ''); setHorseName(''); }} className="text-[#9E9E9E] text-[10px] hover:text-white" data-testid="quick-enter-reset-btn">Reset</button>
              </div>
            )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Rider Info + Classes + Accommodations */}
            <div className="lg:col-span-2 space-y-6">
              {/* Rider Info - Always first */}
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid="rider-info-section">
                <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Rider Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {managedRiders.length > 0 && (
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-[#9E9E9E] mb-1">Entering For</label>
                      <select
                        value={selectedRiderId}
                        onChange={(e) => {
                          setSelectedRiderId(e.target.value);
                          if (e.target.value === 'self') { setRiderName(user?.name || ''); }
                          else { const r = managedRiders.find(r => r.id === e.target.value); if (r) setRiderName(r.name); }
                        }}
                        className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                        data-testid="entry-rider-select"
                      >
                        <option value="self">Myself ({user?.name})</option>
                        {managedRiders.map(r => <option key={r.id} value={r.id}>{r.name}{r.relationship ? ` (${r.relationship})` : ''}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-[#9E9E9E] mb-1">Rider Name *</label>
                    <input type="text" value={riderName} onChange={(e) => setRiderName(e.target.value)}
                      placeholder="Your name" className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                      data-testid="entry-rider-name" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#9E9E9E] mb-1">Horse Name</label>
                    {savedHorses.length > 0 ? (
                      <div className="space-y-2">
                        <select value={horseName} onChange={(e) => setHorseName(e.target.value)}
                          className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                          data-testid="entry-horse-select">
                          <option value="">Select a horse or type below</option>
                          {savedHorses.map(h => <option key={h.id} value={h.name}>{h.name}{h.breed ? ` (${h.breed})` : ''}</option>)}
                        </select>
                        <input type="text" value={horseName} onChange={(e) => setHorseName(e.target.value)}
                          placeholder="Or type horse name" className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#1a1a1a] rounded-md text-white text-xs focus:outline-none focus:border-[#0096FF] placeholder:text-[#555]"
                          data-testid="entry-horse-name" />
                      </div>
                    ) : (
                      <input type="text" value={horseName} onChange={(e) => setHorseName(e.target.value)}
                        placeholder="Horse name" className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                        data-testid="entry-horse-name" />
                    )}
                  </div>
                  {/* v270 — "Save my info" opt-in. Persists rider/horse to
                      this device's localStorage so the next entry (any show)
                      auto-fills without a login. Riders can toggle off to
                      clear on next submit. */}
                  <div className="sm:col-span-2 flex items-start gap-2 pt-1">
                    <input
                      id="save-my-info"
                      type="checkbox"
                      checked={saveMyInfo}
                      onChange={(e) => setSaveMyInfo(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[#0096FF] rounded cursor-pointer"
                      data-testid="save-my-info-toggle"
                    />
                    <label htmlFor="save-my-info" className="text-xs text-[#9E9E9E] leading-snug cursor-pointer flex-1">
                      <span className="font-semibold text-white">Save my info on this device</span>
                      <span className="block text-[10px] text-[#555] mt-0.5">
                        Autofill rider + horse names next time you enter a show. Only saved on this device.
                        {hasSavedInfo && (
                          <>
                            {' '}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                try { localStorage.removeItem('sm_saved_rider_info'); } catch {}
                                setHasSavedInfo(false);
                                setSaveMyInfo(false);
                                toast.success('Saved info cleared');
                              }}
                              className="text-[#FF9500] underline hover:text-[#FFB347]"
                              data-testid="save-my-info-clear"
                            >Clear saved info</button>
                          </>
                        )}
                      </span>
                    </label>
                  </div>
                  {organizations.length > 0 && (
                    <div>
                      <label className="block text-xs text-[#9E9E9E] mb-1">Organization <span className="text-[#555]">(optional)</span></label>
                      <select value={selectedOrgId} onChange={(e) => setSelectedOrgId(e.target.value)}
                        className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                        data-testid="entry-org-select">
                        <option value="">No Organization</option>
                        {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                      </select>
                    </div>
                  )}
                  {Object.values(rolloverMap).some(rollovers => rollovers.some(r => r.rollover_category)) && (
                    <div>
                      <label className="block text-xs text-[#FF9500] mb-1 font-semibold">Age Group</label>
                      <div className="grid grid-cols-3 gap-1.5" data-testid="age-group-selector">
                        {[{ value: 'YOUTH', label: 'Youth' }, { value: 'ADULT', label: 'Adult' }, { value: 'MASTERS', label: 'Masters' }].map(({ value, label }) => (
                          <button key={value} type="button" onClick={() => handleAgeGroupChange(value)} data-testid={`age-group-${value.toLowerCase()}`}
                            className={`px-2 py-2 rounded-md text-xs font-semibold transition-all ${riderAgeGroup === value ? 'bg-[#FF9500] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:border-[#FF9500]/30'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Class Selection */}
              {hiddenLockedCount > 0 && (
                <div className="mb-3 px-3 py-2 bg-[#FACC15]/10 border border-[#FACC15]/40 rounded-md text-xs text-[#FACC15] flex items-center gap-2" data-testid="locked-days-banner">
                  <span>🔒</span>
                  <span>
                    {hiddenLockedCount} class{hiddenLockedCount !== 1 ? 'es' : ''} on locked days {hiddenLockedCount === 1 ? 'is' : 'are'} hidden — entry by producer/qualification only.
                  </span>
                </div>
              )}
              <ClassSelector
                classesByDay={classesByDay} dayKeys={dayKeys} isMultiDay={isMultiDay}
                activeDayTab={activeDayTab} selectedClasses={selectedClasses}
                entryCounts={entryCounts} rolloverMap={rolloverMap}
                selectedRollovers={selectedRollovers} riderAgeGroup={riderAgeGroup}
                practiceCounts={practiceCounts} onSetPracticeCount={(cid, n) => setPracticeCounts(p => ({ ...p, [cid]: n }))}
                onSetActiveDayTab={setActiveDayTab} onToggleClass={toggleClass}
                onToggleRollover={toggleRollover} getEligibleRollovers={getEligibleRollovers}
              />

              {/* Target Race opt-in — placed RIGHT UNDER class entries per
                  user spec (was previously below Stalls/RV which made riders
                  miss it). Only renders when the producer set entry_fee > 0. */}
              {targets.filter(t => Number(t.entry_fee) > 0).length > 0 && (
                <div className="mt-6 bg-[#161616] border border-[#FF375F]/30 rounded-lg p-5" data-testid="target-opt-in-section">
                  <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <span className="text-[#FF375F]">&#9678;</span> Target Race
                  </h3>
                  <p className="text-xs text-[#9E9E9E] mb-4">
                    Optional opt-in for prize money on closest 2 times within a single category (e.g. both Open runs across days).
                  </p>
                  <div className="space-y-2">
                    {targets.filter(t => Number(t.entry_fee) > 0).map(tgt => {
                      const checked = selectedTargetIds.includes(tgt.id);
                      const cats = tgt.categories || [];
                      const eligibleCats = cats.filter(c =>
                        c.class_ids.some(cid => selectedClasses.includes(cid))
                      );
                      // Single-select mode: when a target has multiple
                      // categories, the rider must pick exactly ONE category
                      // their times count toward (per user spec). When a
                      // target has just 1 category, no UI needed — auto-pick.
                      const defaultCat = (eligibleCats[0] || cats[0])?.id;
                      const picks = targetCategoryPicks[tgt.id] || (defaultCat ? [defaultCat] : []);
                      const toggleTarget = () => {
                        setSelectedTargetIds(prev =>
                          checked ? prev.filter(id => id !== tgt.id) : [...prev, tgt.id]
                        );
                        if (!checked && defaultCat) {
                          // Pre-select first eligible category on opt-in.
                          setTargetCategoryPicks(prev => ({
                            ...prev,
                            [tgt.id]: [defaultCat],
                          }));
                        }
                      };
                      // Single-select: clicking a category replaces the pick.
                      const selectCat = (catId) => {
                        setTargetCategoryPicks(prev => ({ ...prev, [tgt.id]: [catId] }));
                      };
                      return (
                        <div
                          key={tgt.id}
                          className={`rounded-lg transition-colors ${
                            checked
                              ? 'bg-[#FF375F]/10 border border-[#FF375F]/40'
                              : 'bg-[#0A0A0A] border border-[#262626] hover:border-[#FF375F]/30'
                          }`}
                          data-testid={`target-opt-row-${tgt.id}`}
                        >
                          <label
                            className="flex items-start gap-3 p-3 cursor-pointer"
                            data-testid={`target-opt-checkbox-${tgt.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={toggleTarget}
                              className="mt-1 w-4 h-4 accent-[#FF375F] flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-semibold text-sm">{tgt.name}</p>
                              <p className="text-[10px] text-[#9E9E9E] mt-0.5">
                                {cats.length} categor{cats.length !== 1 ? 'ies' : 'y'} &middot; {tgt.places_paid} place{tgt.places_paid !== 1 ? 's' : ''} paid
                                {eligibleCats.length > 0 && (
                                  <span className="text-[#34C759]"> &middot; You qualify for {eligibleCats.length} categor{eligibleCats.length !== 1 ? 'ies' : 'y'}</span>
                                )}
                              </p>
                            </div>
                            <span className="text-[#34C759] font-bold text-sm flex-shrink-0">+${Number(tgt.entry_fee).toFixed(0)}</span>
                          </label>
                          {checked && cats.length > 1 && (
                            <div className="px-3 pb-3 pt-1 border-t border-[#FF375F]/20" data-testid={`target-categories-${tgt.id}`}>
                              <p className="text-[10px] text-[#9E9E9E] mb-2 uppercase tracking-wider">Choose ONE category for your times to count toward</p>
                              <div className="space-y-1">
                                {cats.map(cat => {
                                  const catSelected = picks[0] === cat.id;
                                  const enteredInCat = cat.class_ids.filter(cid => selectedClasses.includes(cid)).length;
                                  return (
                                    <label
                                      key={cat.id}
                                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-xs ${
                                        catSelected
                                          ? 'bg-[#FF375F]/10 border border-[#FF375F]/30'
                                          : 'bg-[#0A0A0A] border border-[#262626] hover:border-[#FF375F]/30'
                                      }`}
                                      data-testid={`target-cat-${tgt.id}-${cat.id}`}
                                    >
                                      <input
                                        type="radio"
                                        name={`target-cat-${tgt.id}`}
                                        checked={catSelected}
                                        onChange={() => selectCat(cat.id)}
                                        className="w-3.5 h-3.5 accent-[#FF375F] flex-shrink-0"
                                      />
                                      <span className="flex-1 text-white">{cat.name}</span>
                                      <span className="text-[10px] text-[#9E9E9E]">
                                        {enteredInCat > 0 ? `${enteredInCat}/${cat.class_ids.length} entered` : `${cat.class_ids.length} class${cat.class_ids.length !== 1 ? 'es' : ''}`}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                              {picks.length === 0 && (
                                <p className="text-[10px] text-[#FF3B30] mt-1">Pick a category to opt in.</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <AccommodationsSection
                accommodations={accommodations} stallsRequested={stallsRequested}
                rvRequested={rvRequested} stallCost={stallCost} rvCost={rvCost}
                onSetStalls={setStallsRequested} onSetRv={setRvRequested}
              />

              <SpotPicker showId={showId} onSelectionChange={setSpotSelection} />

              {/* Producer add-on fees: T-Shirt + Tie-Out + Shavings */}
              {(show?.tshirt_enabled || show?.tie_out_fee_enabled || show?.shavings_enabled) && (
                <div className="mt-6 bg-[#161616] border border-[#FF9500]/30 rounded-lg p-5 space-y-4" data-testid="merch-section">
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Add-Ons</h3>

                  {show?.tshirt_enabled && Number(show?.tshirt_price) > 0 && (
                    <div className="space-y-2" data-testid="entry-tshirt-section">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox"
                          checked={tshirtSelection.enabled}
                          onChange={(e) => setTshirtSelection((s) => ({
                            ...s,
                            enabled: e.target.checked,
                            items: e.target.checked && (!s.items || s.items.length === 0) ? [{ qty: 1, size: '' }] : s.items,
                          }))}
                          data-testid="entry-tshirt-checkbox"
                          className="mt-1 w-4 h-4 accent-[#FF9500] flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white font-semibold text-sm">{show.tshirt_label || 'Show T-Shirt'}</p>
                          <p className="text-[10px] text-[#9E9E9E]">${Number(show.tshirt_price).toFixed(0)} per shirt</p>
                        </div>
                      </label>
                      {tshirtSelection.enabled && (
                        <div className="pl-7 space-y-2">
                          {(tshirtSelection.items || []).map((item, idx) => (
                            <div key={idx} className="grid grid-cols-[80px_1fr_28px] gap-2 items-end" data-testid={`entry-tshirt-row-${idx}`}>
                              <div>
                                {idx === 0 && <label className="block text-[10px] text-[#9E9E9E] mb-1">Qty</label>}
                                <input type="number" min="1" max="20" value={item.qty || 1}
                                  onChange={(e) => setTshirtSelection((s) => {
                                    const items = [...(s.items || [])];
                                    items[idx] = { ...items[idx], qty: Math.max(1, parseInt(e.target.value, 10) || 1) };
                                    return { ...s, items };
                                  })}
                                  className="w-full px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
                                  data-testid={`entry-tshirt-qty-${idx}`} />
                              </div>
                              <div>
                                {idx === 0 && <label className="block text-[10px] text-[#9E9E9E] mb-1">Size</label>}
                                <select value={item.size}
                                  onChange={(e) => setTshirtSelection((s) => {
                                    const items = [...(s.items || [])];
                                    items[idx] = { ...items[idx], size: e.target.value };
                                    return { ...s, items };
                                  })}
                                  className="w-full px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
                                  data-testid={`entry-tshirt-size-${idx}`}>
                                  <option value="">Select size...</option>
                                  {(show.tshirt_sizes || ['S', 'M', 'L', 'XL', 'XXL']).map((sz) => (
                                    <option key={sz} value={sz}>{sz}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex items-end">
                                {(tshirtSelection.items || []).length > 1 ? (
                                  <button
                                    type="button"
                                    onClick={() => setTshirtSelection((s) => ({
                                      ...s,
                                      items: (s.items || []).filter((_, i) => i !== idx),
                                    }))}
                                    className="w-7 h-[34px] flex items-center justify-center text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded transition-colors"
                                    title="Remove this shirt"
                                    data-testid={`entry-tshirt-remove-${idx}`}
                                  >
                                    ×
                                  </button>
                                ) : <div className="w-7" />}
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setTshirtSelection((s) => ({
                              ...s,
                              items: [...(s.items || []), { qty: 1, size: '' }],
                            }))}
                            className="text-[#FF9500] text-xs font-semibold hover:text-[#FF9500]/80 transition-colors"
                            data-testid="entry-tshirt-add-row"
                          >
                            + Add another shirt
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {tieOutEnabled && (
                    <div className="flex items-start gap-3" data-testid="entry-tie-out-section">
                      <div className="mt-1 w-4 h-4 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">{show.tie_out_fee_label || 'Tie-Out Fee'} <span className="text-[10px] text-[#FF9500] font-normal ml-1">auto-billed</span></p>
                        <p className="text-[10px] text-[#9E9E9E]">
                          ${Number(show.tie_out_fee_price).toFixed(0)} per horse, per day · charged for horses without a stall
                        </p>
                        {horsesNeedingTieOut > 0 && !tieOutWaived ? (
                          <p className="text-[11px] text-white mt-1" data-testid="entry-tie-out-breakdown">
                            {horsesNeedingTieOut} horse{horsesNeedingTieOut === 1 ? '' : 's'} × {daysEntered} day{daysEntered === 1 ? '' : 's'} × ${Number(show.tie_out_fee_price).toFixed(0)}
                            <span className="ml-2 text-[#34C759] font-semibold" data-testid="entry-tie-out-total">= ${tieOutTotal.toFixed(2)}</span>
                          </p>
                        ) : tieOutWaived ? (
                          <p className="text-[11px] text-[#FF9500] mt-1" data-testid="entry-tie-out-waived">
                            Tie-out fee waived — you have a stall not on this checkout
                          </p>
                        ) : existingStallCount > 0 ? (
                          <p className="text-[11px] text-[#34C759] mt-1" data-testid="entry-tie-out-existing-stalls">
                            You already have {existingStallCount} stall{existingStallCount === 1 ? '' : 's'} reserved for this show — no tie-out fee.
                          </p>
                        ) : (
                          <p className="text-[11px] text-[#34C759] mt-1" data-testid="entry-tie-out-covered">
                            {numHorses === 0 ? 'Add a horse first' : 'All horses covered by stalls — no tie-out fee'}
                          </p>
                        )}
                        {horsesNeedingTieOut > 0 && (
                          <label className="flex items-center gap-2 mt-2 cursor-pointer text-[11px] text-[#E5E5E5] hover:text-white">
                            <input
                              type="checkbox"
                              checked={tieOutWaived}
                              onChange={(e) => setTieOutWaived(e.target.checked)}
                              className="rounded"
                              data-testid="tie-out-waive-checkbox"
                            />
                            <span>I already have a stall not on this checkout — skip the tie-out fee</span>
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  {show?.shavings_enabled && Number(show?.shavings_price_per_bag) > 0 && (
                    <div className="flex items-start gap-3" data-testid="entry-shavings-section">
                      <div className="mt-1 w-4 h-4 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">{show.shavings_label || 'Shavings'}</p>
                        <p className="text-[10px] text-[#9E9E9E] mb-2">${Number(show.shavings_price_per_bag).toFixed(0)} per bag</p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setShavingsQty(q => Math.max(0, q - 1))}
                            disabled={shavingsQty === 0}
                            className="w-8 h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white hover:border-[#FF9500] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-base"
                            data-testid="entry-shavings-decrement"
                          >−</button>
                          <input
                            type="number"
                            min="0"
                            max="999"
                            value={shavingsQty}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              setShavingsQty(Number.isFinite(v) && v >= 0 ? Math.min(v, 999) : 0);
                            }}
                            className="w-16 text-center px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
                            data-testid="entry-shavings-qty"
                          />
                          <button
                            type="button"
                            onClick={() => setShavingsQty(q => Math.min(999, q + 1))}
                            className="w-8 h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white hover:border-[#FF9500] flex items-center justify-center text-base"
                            data-testid="entry-shavings-increment"
                          >+</button>
                          <span className="text-[10px] text-[#9E9E9E] ml-2">bag{shavingsQty === 1 ? '' : 's'}</span>
                          {shavingsQty > 0 && (
                            <span className="ml-auto text-xs text-[#34C759] font-semibold" data-testid="entry-shavings-total">
                              ${(shavingsQty * Number(show.shavings_price_per_bag)).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Target Race opt-in (sidepot-style fee).
                  Rendered higher up — right under class entries. */}

              {/* Optional Sponsorship — rider tips the producer with any amount. */}
              {show && (
                <div className="mt-6 bg-[#161616] border border-[#FF3B30]/30 rounded-lg p-5" data-testid="entry-sponsor-section">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="entry-sponsor-toggle">
                    <input
                      type="checkbox"
                      checked={sponsorEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSponsorEnabled(checked);
                        if (checked && !sponsorAmount) {
                          // Pre-fill with the show's minimum so the toggle
                          // doesn't render a $0 line item.
                          setSponsorAmount(String(Number(show?.sponsorship_minimum || 5) || 5));
                        }
                      }}
                      className="mt-1 w-4 h-4 accent-[#FF3B30] flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                        <span className="text-[#FF3B30]">&#9829;</span> Sponsor this Show
                      </h3>
                      <p className="text-xs text-[#9E9E9E] mt-0.5">Add an optional sponsorship to your bill — the producer will be notified directly.</p>
                    </div>
                  </label>
                  {sponsorEnabled && (
                    <div className="mt-4 space-y-3 pl-7">
                      <div className="grid grid-cols-4 gap-2">
                        {[25, 50, 100, 250].map(preset => (
                          <button
                            type="button"
                            key={preset}
                            onClick={() => setSponsorAmount(String(preset))}
                            className={`px-2 py-2 rounded-md text-sm font-semibold transition-colors ${
                              Number(sponsorAmount) === preset
                                ? 'bg-[#FF3B30] text-white'
                                : 'bg-[#0A0A0A] border border-[#262626] text-white hover:border-[#FF3B30]/50'
                            }`}
                            data-testid={`entry-sponsor-preset-${preset}`}
                          >
                            ${preset}
                          </button>
                        ))}
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] mb-1 font-semibold">
                          Custom Amount ($) <span className="text-[#555] normal-case font-normal">(min ${Number(show?.sponsorship_minimum || 1).toFixed(2)})</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min={show?.sponsorship_minimum || 1}
                          value={sponsorAmount}
                          onChange={(e) => setSponsorAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm focus:border-[#FF3B30] focus:outline-none"
                          data-testid="entry-sponsor-amount"
                        />
                      </div>
                      {sponsorTotal > 0 && Number(sponsorAmount) < Number(show?.sponsorship_minimum || 1) && (
                        <p className="text-[10px] text-[#FF3B30]">
                          Minimum sponsorship for this show is ${Number(show?.sponsorship_minimum || 1).toFixed(2)}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: Checkout Summary (sticky) */}
            <div>
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 sticky top-24">
                <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Entry Summary</h3>

                {/* Added entries */}
                {entries.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {entries.map((entry, idx) => {
                      const f = calcEntryFees(entry.selectedClasses, entry.selectedRollovers);
                      return (
                        <div key={idx} className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2" data-testid={`entries-entry-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <p className="text-white text-sm font-semibold truncate">{entry.riderName}</p>
                              {entry.horseName && <p className="text-[#9E9E9E] text-xs truncate">{entry.horseName}</p>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[#0096FF] text-xs font-bold">{entry.selectedClasses.length} class{entry.selectedClasses.length !== 1 ? 'es' : ''}</span>
                              <button onClick={() => removeEntry(idx)} className="text-[#FF3B30] text-xs hover:text-[#FF3B30]/80" data-testid={`entries-remove-${idx}`}>&times;</button>
                            </div>
                          </div>
                          <p className="text-[#9E9E9E] text-[10px] mt-1">${(f.classFees + f.rolloverFees).toFixed(2)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* "Already paid this show" reassurance banner — shown
                    after the rider's first successful checkout for this
                    show. Lets them know the totals below reflect ONLY
                    the new charges, not their full show ledger. Wired to
                    /my-checkout-status which also gates other once-per-
                    show fees (processing fee, tie-out), keeping the
                    source of truth single. */}
                {alreadyPaidTotal > 0 && (
                  <div
                    className="mb-3 px-3 py-2 bg-[#34C759]/10 border border-[#34C759]/30 rounded-md flex items-start gap-2"
                    data-testid="already-paid-banner"
                  >
                    <div className="flex-1 text-xs leading-relaxed">
                      <p className="text-[#34C759] font-semibold">
                        You&apos;ve already paid ${alreadyPaidTotal.toFixed(2)} for this show.
                      </p>
                      <p className="text-[#9E9E9E] mt-0.5">
                        This checkout is for new charges only.
                        {lastPaidSessionId && (
                          <>
                            {' '}
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/receipt/${lastPaidSessionId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#0096FF] underline hover:text-[#0096FF]/80"
                              data-testid="already-paid-banner-receipt-link"
                            >
                              Download last receipt
                            </a>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Current form summary */}
                {riderName && selectedClasses.length > 0 && (
                  <div className="mb-3 pb-3 border-b border-[#262626]">
                    <p className="text-[#9E9E9E] text-xs">{riderName}{horseName ? ` on ${horseName}` : ''} — {selectedClasses.length} class{selectedClasses.length !== 1 ? 'es' : ''}</p>
                  </div>
                )}

                <div className="space-y-1.5 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-[#9E9E9E]">Classes ({entriesClassCount + selectedClasses.length})</span>
                    <span className="text-white">${(classFees + entries.reduce((s, e) => s + calcEntryFees(e.selectedClasses, e.selectedRollovers, e.practiceCounts || {}).classFees, 0)).toFixed(2)}</span>
                  </div>
                  {(totalRolloverCount + entriesRolloverCount) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Rollovers ({totalRolloverCount + entriesRolloverCount})</span>
                      <span className="text-white">${(rolloverFees + entries.reduce((s, e) => s + calcEntryFees(e.selectedClasses, e.selectedRollovers).rolloverFees, 0)).toFixed(2)}</span>
                    </div>
                  )}
                  {targetFees > 0 && (
                    <div className="flex justify-between" data-testid="summary-target-fees-row">
                      <span className="text-[#FF375F]">Target Race ({selectedTargetIds.length})</span>
                      <span className="text-white">${targetFees.toFixed(2)}</span>
                    </div>
                  )}
                  {sponsorTotal > 0 && (
                    <div className="flex justify-between" data-testid="summary-sponsor-row">
                      <span className="text-[#FF3B30] flex items-center gap-1"><span>&#9829;</span> Sponsorship</span>
                      <span className="text-white">${sponsorTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {tshirtTotal > 0 && (
                    <div className="flex justify-between" data-testid="summary-tshirt-row">
                      <span className="text-[#FF9500]">
                        {show?.tshirt_label || 'Show T-Shirt'} (
                        {(tshirtSelection.items || [])
                          .filter((it) => (parseInt(it.qty, 10) || 0) > 0)
                          .map((it) => `${it.qty}×${it.size || '–'}`)
                          .join(', ') || tshirtTotalQty}
                        )
                      </span>
                      <span className="text-white">${tshirtTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {tieOutTotal > 0 && (
                    <div className="flex justify-between" data-testid="summary-tie-out-row">
                      <span className="text-[#FF9500]">{show?.tie_out_fee_label || 'Tie-Out Fee'}</span>
                      <span className="text-white">${tieOutTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {accommodationFees > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">
                        Accommodations
                        {stallsRequested > 0 && <span className="text-[10px]"> ({stallsRequested} stall{stallsRequested > 1 ? 's' : ''})</span>}
                        {rvRequested > 0 && <span className="text-[10px]"> ({rvRequested} RV)</span>}
                        {spotSelection.spots.length > 0 && <span className="text-[10px]"> ({spotSelection.spots.length} spot{spotSelection.spots.length > 1 ? 's' : ''})</span>}
                      </span>
                      <span className="text-white">${accommodationFees.toFixed(2)}</span>
                    </div>
                  )}
                  {processingFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Office/Rider Fee {daysEntered > 1 ? `(${daysEntered} days)` : ''}</span>
                      <span className="text-white" data-testid="entry-processing-fee">${processingFee.toFixed(2)}</span>
                    </div>
                  )}
                  {procFeeAlreadyPaid && baseProcessingFee > 0 && (
                    <div className="flex justify-between text-[#34C759]" data-testid="entry-processing-fee-already-paid">
                      <span>Office/Rider Fee</span>
                      <span>Already paid · $0</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[#FACC15]">
                    <span>Service Fee</span>
                    <span>${serviceFee.toFixed(2)}</span>
                  </div>
                  {stripeFee > 0 && (
                    <div className="flex justify-between text-[#9E9E9E]">
                      <span>C.C. Fee / Payment Processing</span>
                      <span>${stripeFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-[#262626]">
                    <span className="text-white font-bold">Total</span>
                    <span className="text-[#0096FF] font-bold text-lg" data-testid="entry-total">${totalFees.toFixed(2)}</span>
                  </div>
                </div>

                {/* Add Another Rider/Horse */}
                {selectedClasses.length > 0 && riderName.trim() && (
                  <button onClick={addEntry}
                    className="w-full py-2.5 mb-3 bg-[#FF9500]/15 hover:bg-[#FF9500]/25 text-[#FF9500] border border-[#FF9500]/30 font-semibold rounded-md text-sm transition-colors"
                    data-testid="add-another-entry-btn">
                    + Add Another Rider/Horse
                  </button>
                )}

                {/* Special Request / Notes */}
                <div className="mb-4" data-testid="special-request-section">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#9E9E9E] mb-1.5">
                    Special Request / Notes <span className="text-[#555] normal-case font-normal">— optional</span>
                  </label>
                  <textarea
                    value={specialRequest}
                    onChange={(e) => setSpecialRequest(e.target.value.slice(0, 500))}
                    placeholder="e.g. Bringing a stallion — needs end stall. Please run my 2D horse first."
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm placeholder:text-[#555] focus:border-[#0096FF] focus:outline-none resize-none"
                    data-testid="special-request-input"
                  />
                  <p className="text-[10px] text-[#555] mt-1">
                    {specialRequest.length}/500 — the producer will see this on each of your entries.
                  </p>
                </div>

                {/* Waiver */}
                <div className="mb-4">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={waiverAccepted} onChange={(e) => setWaiverAccepted(e.target.checked)}
                      className="mt-1 accent-[#0096FF]" data-testid="waiver-checkbox" />
                    <span className="text-[#9E9E9E] text-xs leading-relaxed">
                      I accept full responsibility for any injuries and release the show producers and venue from all liability.
                    </span>
                  </label>
                </div>

                {/* Submit */}
                {!user ? (
                  <button onClick={() => navigate('/login')} className="w-full py-3 bg-[#0096FF] text-white font-semibold rounded-md text-sm hover:bg-[#00D4FF] transition-colors" data-testid="login-to-enter-btn">Log In to Enter</button>
                ) : (() => {
                  const hasAccommodations = spotSelection.spots.length > 0 || stallsRequested > 0 || rvRequested > 0;
                  const hasClasses = selectedClasses.length > 0 || entries.length > 0;
                  const canSubmit = hasClasses || hasAccommodations;
                  const accommodationsOnly = !hasClasses && hasAccommodations;
                  const label = submitting ? 'Submitting...'
                    : accommodationsOnly ? `Reserve Stalls/RV ($${totalFees.toFixed(2)})`
                    : entries.length > 0 ? `Submit All ${entries.length + (selectedClasses.length > 0 ? 1 : 0)} Entries ($${totalFees.toFixed(2)})`
                    : `Submit Entry ($${totalFees.toFixed(2)})`;
                  return (
                    <button onClick={handleSubmit}
                      disabled={submitting || !canSubmit || !waiverAccepted || !riderName.trim()}
                      className="w-full py-3 bg-[#34C759] text-white font-semibold rounded-md text-sm hover:bg-[#2FB350] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-entry-btn">
                      {label}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EntryForm;
